import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead, { SEO_CONFIG } from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, Shield, TrendingUp, Clock, Users, CheckCircle, 
  Building, Zap, Target, Eye, Home, ChevronDown, ChevronRight,
  ArrowDown, Star, Search, Key, Play, Sparkles
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get('/properties/featured');
        setFeaturedProperties(response.data);
      } catch (error) {
        console.error('Error fetching featured properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead {...SEO_CONFIG.home} />
      <Header />

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative min-h-[90vh] flex items-center section-white" data-testid="hero-section">
        <div className="container-apple w-full py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Left Content */}
            <div className="animate-fade-in-up">
              {/* Overline */}
              <div className="inline-flex items-center space-x-2 mb-8">
                <div className="w-2 h-2 bg-[#0071e3] rounded-full"></div>
                <span className="text-[11px] sm:text-[13px] font-medium text-[#86868B] uppercase tracking-wider whitespace-nowrap">
                  Prima piattaforma immobiliare innovativa
                </span>
              </div>

              {/* Main Headline - SEO Optimized H1 */}
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter text-[#1D1D1F] leading-[1.05] mb-6">
                Vendi casa
                <span className="block">velocemente</span>
                <span className="block text-[#0071e3]">al miglior prezzo</span>
              </h1>

              {/* Subheadline - SEO Optimized */}
              <p className="text-[19px] leading-relaxed text-[#86868B] mb-10 max-w-lg">
                Vendi o acquista casa senza perdere tempo. WEQASA è la piattaforma immobiliare innovativa che garantisce trasparenza, sicurezza e il miglior prezzo di mercato.
              </p>

              {/* Two CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button 
                  className="btn-apple-primary"
                  onClick={() => navigate('/registrati/proprietario')}
                  data-testid="hero-seller-cta"
                >
                  <Home className="mr-2 h-5 w-5" strokeWidth={1.5} />
                  Inserisci Immobile
                </button>
                <button 
                  className="btn-apple-secondary"
                  onClick={() => navigate('/immobili')}
                  data-testid="hero-buyer-cta"
                >
                  <Search className="mr-2 h-5 w-5" strokeWidth={1.5} />
                  Trova Immobile
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-8 text-[14px] text-[#86868B]">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" strokeWidth={1.5} />
                  <span>100% Sicuro</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" strokeWidth={1.5} />
                  <span>Agenti Verificati</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" strokeWidth={1.5} />
                  <span>Gratuito</span>
                </div>
              </div>
            </div>

            {/* Right - Hero Image */}
            <div className="relative animate-fade-in-up animate-delay-200">
              <div className="relative rounded-3xl overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.15)]">
                <img 
                  src="https://images.unsplash.com/photo-1763854450561-10af72250ba9?w=800&q=80"
                  alt="Elegante palazzo storico nel centro di Roma"
                  className="w-full h-[500px] object-cover"
                />
                {/* Floating Stats Card */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-xl rounded-2xl p-6 shadow-lg">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-[#1D1D1F]">+45%</div>
                      <div className="text-[12px] text-[#86868B]">Vendita veloce</div>
                    </div>
                    <div className="border-x border-black/5">
                      <div className="text-2xl font-bold text-[#1D1D1F]">98%</div>
                      <div className="text-[12px] text-[#86868B]">Soddisfatti</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-[#1D1D1F]">+12%</div>
                      <div className="text-[12px] text-[#86868B]">Prezzo finale</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section id="how-it-works" className="section-neutral section-spacing" data-testid="how-it-works-section">
        <div className="container-apple">
          {/* Section Header */}
          <div className="text-center mb-20">
            <span className="text-[14px] font-medium text-[#0071e3] uppercase tracking-wider">
              Come Funziona
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#1D1D1F] mt-4 mb-6">
              Tre semplici passaggi.
            </h2>
            <p className="text-[17px] text-[#86868B] max-w-xl mx-auto">
              Un processo chiaro e trasparente per vendere o acquistare il tuo immobile.
            </p>
          </div>

          {/* Steps Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                number: '01',
                title: 'Inserisci il tuo immobile',
                description: 'Carica foto e dettagli in pochi minuti. La nostra piattaforma ti guida passo dopo passo.',
                icon: Home
              },
              {
                number: '02',
                title: 'Ricevi proposte',
                description: 'Gli agenti della tua zona ti contattano con proposte personalizzate e competitive.',
                icon: Users
              },
              {
                number: '03',
                title: 'Gara di Vendita',
                description: 'Il tuo immobile viene venduto attraverso un sistema trasparente e competitivo.',
                icon: TrendingUp
              }
            ].map((step, index) => (
              <div 
                key={index}
                className="premium-card p-10 text-center"
              >
                <div className="w-16 h-16 bg-[#F5F5F7] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <step.icon className="h-7 w-7 text-[#1D1D1F]" strokeWidth={1.5} />
                </div>
                <div className="text-[12px] font-semibold text-[#0071e3] mb-3">{step.number}</div>
                <h3 className="text-xl font-semibold tracking-tight text-[#1D1D1F] mb-4">
                  {step.title}
                </h3>
                <p className="text-[15px] text-[#86868B] leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <button 
              className="btn-apple-primary"
              onClick={() => navigate('/registrati/proprietario')}
              data-testid="how-it-works-cta"
            >
              Inizia Ora
              <ArrowRight className="ml-2 h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </section>

      {/* ==================== BENEFITS BENTO GRID ==================== */}
      <section className="section-white section-spacing" data-testid="benefits-section">
        <div className="container-apple">
          {/* Section Header */}
          <div className="text-center mb-20">
            <span className="text-[14px] font-medium text-[#0071e3] uppercase tracking-wider">
              I Vantaggi
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#1D1D1F] mt-4 mb-6">
              Perché scegliere WEQASA.
            </h2>
            <p className="text-[17px] text-[#86868B] max-w-xl mx-auto">
              Una piattaforma progettata per massimizzare il valore delle tue transazioni.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Large Feature Card */}
            <div className="lg:col-span-2 premium-card p-10 bg-gradient-to-br from-[#0A2540] to-[#1a4a6e]">
              <div className="flex flex-col h-full">
                <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6">
                  <Zap className="h-7 w-7 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-semibold tracking-tight text-white mb-4">
                  Vendita più veloce
                </h3>
                <p className="text-[17px] text-white/70 leading-relaxed mb-8">
                  Il sistema di Gara di Vendita accelera l'incontro tra domanda e offerta, riducendo drasticamente i tempi medi di vendita del 45%.
                </p>
                <div className="mt-auto">
                  <div className="text-5xl font-bold text-[#0071e3]">45%</div>
                  <div className="text-[14px] text-white/50 mt-1">più veloce della media</div>
                </div>
              </div>
            </div>

            {/* Security Card */}
            <div className="premium-card p-8">
              <div className="w-12 h-12 bg-[#F5F5F7] rounded-xl flex items-center justify-center mb-5">
                <Shield className="h-6 w-6 text-[#1D1D1F]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold tracking-tight text-[#1D1D1F] mb-3">
                Massima sicurezza
              </h3>
              <p className="text-[15px] text-[#86868B] leading-relaxed">
                Acquirenti verificati, transazioni protette. Ogni operazione è tracciata e sicura.
              </p>
            </div>

            {/* Price Card */}
            <div className="premium-card p-8">
              <div className="w-12 h-12 bg-[#F5F5F7] rounded-xl flex items-center justify-center mb-5">
                <TrendingUp className="h-6 w-6 text-[#1D1D1F]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold tracking-tight text-[#1D1D1F] mb-3">
                Prezzo ottimizzato
              </h3>
              <p className="text-[15px] text-[#86868B] leading-relaxed">
                La competizione tra acquirenti può aumentare il valore finale del tuo immobile.
              </p>
            </div>

            {/* Control Card */}
            <div className="premium-card p-8">
              <div className="w-12 h-12 bg-[#F5F5F7] rounded-xl flex items-center justify-center mb-5">
                <Eye className="h-6 w-6 text-[#1D1D1F]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold tracking-tight text-[#1D1D1F] mb-3">
                Controllo totale
              </h3>
              <p className="text-[15px] text-[#86868B] leading-relaxed">
                Scegli l'agente, approvi ogni passaggio. Dashboard in tempo reale.
              </p>
            </div>

            {/* AI Card */}
            <div className="premium-card p-8 bg-[#FBFBF9]">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-5 shadow-sm">
                <Sparkles className="h-6 w-6 text-[#0071e3]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold tracking-tight text-[#1D1D1F] mb-3">
                Virtual Tour AI
              </h3>
              <p className="text-[15px] text-[#86868B] leading-relaxed">
                Genera video tour cinematografici del tuo immobile con l'intelligenza artificiale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FOR BUYERS SECTION ==================== */}
      <section className="section-warm section-spacing">
        <div className="container-apple">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <div className="rounded-3xl overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)]">
                <img 
                  src="https://images.unsplash.com/photo-1775401289843-7ecd56a39f1d?w=800&q=80"
                  alt="Palazzo residenziale nel centro storico di Roma"
                  className="w-full h-[450px] object-cover"
                />
              </div>
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <span className="text-[14px] font-medium text-[#0A2540] uppercase tracking-wider">
                Per gli Acquirenti
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#1D1D1F] mt-4 mb-6">
                Trova la casa dei tuoi sogni.
              </h2>
              <p className="text-[17px] text-[#86868B] leading-relaxed mb-8">
                Accedi a immobili selezionati e partecipa alle Gare di Vendita. Un modo trasparente e competitivo per acquistare la tua prossima casa.
              </p>

              <ul className="space-y-4 mb-10">
                {[
                  'Immobili verificati e documentati',
                  'Processo di acquisto trasparente',
                  'Supporto dedicato in ogni fase',
                  'Nessun costo nascosto'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-[#0A2540] rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-3 w-3 text-white" strokeWidth={2} />
                    </div>
                    <span className="text-[15px] text-[#1D1D1F]">{item}</span>
                  </li>
                ))}
              </ul>

              <button 
                className="btn-apple-secondary"
                onClick={() => navigate('/immobili')}
                data-testid="buyers-section-cta"
              >
                Esplora gli Immobili
                <ArrowRight className="ml-2 h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FEATURED PROPERTIES ==================== */}
      {featuredProperties.length > 0 && (
        <section className="section-white section-spacing" data-testid="featured-properties">
          <div className="container-apple">
            <div className="flex justify-between items-end mb-12">
              <div>
                <span className="text-[14px] font-medium text-[#0071e3] uppercase tracking-wider">
                  In Evidenza
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#1D1D1F] mt-4">
                  Immobili selezionati.
                </h2>
              </div>
              <Link 
                to="/immobili" 
                className="hidden md:flex items-center gap-2 text-[15px] font-medium text-[#0A2540] hover:text-[#061A2D] transition-colors"
              >
                Vedi tutti
                <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.slice(0, 3).map((property) => (
                <Link 
                  key={property.property_id} 
                  to={`/immobili/${property.property_id}`}
                  className="property-card group"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={property.images?.[0] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600'}
                      alt={property.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {property.has_active_gara && (
                      <div className="absolute top-4 left-4 bg-[#0071e3] text-white text-[12px] font-semibold px-3 py-1.5 rounded-full">
                        Gara Attiva
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="text-[13px] text-[#86868B] mb-2">
                      {property.city} • {property.property_type}
                    </div>
                    <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2 line-clamp-1">
                      {property.title}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-[#1D1D1F]">
                        {formatPrice(property.auction_start_price || property.estimated_value)}
                      </span>
                      <span className="text-[13px] text-[#86868B]">
                        {property.sqm} m² • {property.rooms} locali
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12 md:hidden">
              <Link 
                to="/immobili" 
                className="inline-flex items-center gap-2 text-[15px] font-medium text-[#0A2540]"
              >
                Vedi tutti gli immobili
                <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ==================== CTA SECTION ==================== */}
      <section className="section-neutral section-spacing">
        <div className="container-apple">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F] mb-6">
              Pronto a iniziare?
            </h2>
            <p className="text-[19px] text-[#86868B] mb-10">
              Unisciti a migliaia di proprietari e acquirenti che hanno già scelto WEQASA per le loro transazioni immobiliari.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="btn-apple-primary"
                onClick={() => navigate('/registrati/proprietario')}
              >
                <Home className="mr-2 h-5 w-5" strokeWidth={1.5} />
                Inserisci Immobile
              </button>
              <button 
                className="btn-apple-outline"
                onClick={() => navigate('/come-funziona')}
              >
                Scopri di più
                <ArrowRight className="ml-2 h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
